import React, { useState, useEffect } from 'react';

interface TransactionRow {
  id: number;
  date: string;
  description: string;
  quantity: number;
  unitPrice: number;
  supplyAmount: number;
  tax: number;
  total: number;
}

const emptyRow = (): TransactionRow => ({
  id: Date.now() + Math.random(),
  date: new Date().toISOString().slice(0, 10),
  description: '',
  quantity: 1,
  unitPrice: 0,
  supplyAmount: 0,
  tax: 0,
  total: 0,
});

type Client = { id: string; name: string; biz_no: string; manager: string; phone: string };

function authHeaders(): Record<string, string> {
  return { 'Content-Type': 'application/json', Authorization: `Bearer ${sessionStorage.getItem('admin-token') || ''}` };
}

function TransactionStatement() {
  const [companyName, setCompanyName] = useState('제주로부터');
  const [bizNo, setBizNo] = useState('238-77-00174');
  const [representative, setRepresentative] = useState('진정은');
  const [companyPhone, setCompanyPhone] = useState('010-5850-4634');
  const [address, setAddress] = useState('제주특별자치도 제주시 탑동로15길 25');
  const [clientName, setClientName] = useState('');
  const [clientBizNo, setClientBizNo] = useState('');
  const [clientManager, setClientManager] = useState('');
  const [clientPhone, setClientPhone] = useState('');
  const [rows, setRows] = useState<TransactionRow[]>([emptyRow()]);
  const [toast, setToast] = useState('');
  const [taxType, setTaxType] = useState<'과세' | '면세'>('과세');
  const [clients, setClients] = useState<Client[]>([]);
  const [showClientMgr, setShowClientMgr] = useState(false);
  const [notes, setNotes] = useState(
`1. 본 거래내역서의 유효기간은 발행일로부터 30일입니다.
2. 대금 결제는 거래내역서 발행일로부터 3일 이내에 완료하여야 합니다.
3. 상품의 하자가 있는 경우, 공급자는 교환 또는 환불 처리합니다.
4. 입금 계좌: 농협 302-2094-9058-11 진정은`
  );

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(''), 2500);
  };

  // 거래처 로드
  useEffect(() => {
    fetch('/api/clients', { headers: authHeaders() })
      .then(r => r.ok ? r.json() : [])
      .then(d => setClients(Array.isArray(d) ? d : []));
  }, []);

  const selectClient = (c: Client) => {
    setClientName(c.name);
    setClientBizNo(c.biz_no);
    setClientManager(c.manager);
    setClientPhone(c.phone);
  };

  const saveCurrentClient = async () => {
    if (!clientName) { alert('거래처명을 입력해주세요'); return; }
    const existing = clients.find(c => c.name === clientName);
    if (existing) {
      await fetch(`/api/clients/${existing.id}`, { method: 'PATCH', headers: authHeaders(), body: JSON.stringify({ name: clientName, biz_no: clientBizNo, manager: clientManager, phone: clientPhone }) });
      showToast('거래처 정보가 수정되었습니다');
    } else {
      await fetch('/api/clients', { method: 'POST', headers: authHeaders(), body: JSON.stringify({ name: clientName, biz_no: clientBizNo, manager: clientManager, phone: clientPhone }) });
      showToast('거래처가 저장되었습니다');
    }
    const res = await fetch('/api/clients', { headers: authHeaders() });
    setClients(await res.json());
  };

  const deleteClient = async (id: string) => {
    if (!window.confirm('거래처를 삭제할까요?')) return;
    await fetch(`/api/clients/${id}`, { method: 'DELETE', headers: authHeaders() });
    const res = await fetch('/api/clients', { headers: authHeaders() });
    setClients(await res.json());
    showToast('거래처가 삭제되었습니다');
  };

  const updateRow = (id: number, field: keyof TransactionRow, value: string | number) => {
    setRows(prev => prev.map(r => {
      if (r.id !== id) return r;
      const updated = { ...r, [field]: value };
      // 자동 계산
      const supply = updated.quantity * updated.unitPrice;
      updated.supplyAmount = supply;
      updated.tax = taxType === '과세' ? Math.round(supply * 0.1) : 0;
      updated.total = supply + updated.tax;
      return updated;
    }));
  };

  const addRow = () => setRows(prev => [...prev, emptyRow()]);

  const removeRow = (id: number) => {
    if (rows.length <= 1) return;
    setRows(prev => prev.filter(r => r.id !== id));
  };

  const grandSupply = rows.reduce((s, r) => s + r.supplyAmount, 0);
  const grandTax = rows.reduce((s, r) => s + r.tax, 0);
  const grandTotal = rows.reduce((s, r) => s + r.total, 0);

  const fmt = (n: number) => n.toLocaleString();

  const downloadExcel = async () => {
    const data = {
      companyName, bizNo, representative, companyPhone, address,
      clientName, clientBizNo, clientManager, clientPhone, taxType, notes,
      rows: rows.map(r => ({
        date: r.date,
        description: r.description,
        quantity: r.quantity,
        unitPrice: r.unitPrice,
        supplyAmount: r.supplyAmount,
        tax: r.tax,
        total: r.total,
      })),
      grandSupply, grandTax, grandTotal,
    };

    const res = await fetch('/api/transaction/export', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${sessionStorage.getItem('admin-token') || ''}`,
      },
      body: JSON.stringify(data),
    });

    if (!res.ok) { alert('다운로드 실패'); return; }
    const blob = await res.blob();
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `거래내역서_${clientName || '미지정'}.xlsx`;
    a.click();
    URL.revokeObjectURL(a.href);
    showToast('거래내역서가 다운로드되었습니다');
  };

  const downloadPDF = () => {
    const win = window.open('', '_blank', 'width=800,height=600');
    if (!win) { alert('팝업이 차단되었습니다. 팝업 허용 후 다시 시도해주세요.'); return; }

    const today = new Date();
    const pdfDate = `${today.getFullYear()}.${String(today.getMonth() + 1).padStart(2, '0')}.${String(today.getDate()).padStart(2, '0')}`;
    const pdfTitle = `[제주로부터-${clientName || '미지정'}] ${pdfDate} 거래내역서`;

    const html = `<!DOCTYPE html>
<html lang="ko">
<head>
<meta charset="UTF-8">
<title>${pdfTitle}</title>
<style>
  @page { size: A4; margin: 20mm; margin-top: 15mm; margin-bottom: 15mm; }
  * { margin: 0; padding: 0; box-sizing: border-box; }
  body { font-family: -apple-system, 'Noto Sans KR', sans-serif; font-size: 12px; color: #333; }
  .title { background: #8B5A3C; color: #fff; text-align: center; padding: 14px; font-size: 22px; font-weight: bold; letter-spacing: 12px; }
  .info-table { width: 100%; border-collapse: collapse; margin-top: 16px; }
  .info-table td { border: 1px solid #ccc; padding: 6px 10px; font-size: 11px; }
  .info-table .label { background: #f5f5f5; font-weight: bold; width: 80px; }
  .info-table .label-client { background: #fffbeb; font-weight: bold; width: 80px; }
  .main-table { width: 100%; border-collapse: collapse; margin-top: 4px; }
  .main-table th { background: #f5f5f5; border: 1px solid #ccc; padding: 8px 6px; font-size: 11px; font-weight: bold; }
  .main-table td { border: 1px solid #ccc; padding: 7px 6px; font-size: 11px; }
  .main-table .right { text-align: right; }
  .main-table .center { text-align: center; }
  .total-row { background: #f9f9f9; font-weight: bold; }
  .notes { margin-top: 20px; border: 1px solid #ccc; padding: 12px; }
  .notes h3 { font-size: 11px; font-weight: bold; background: #f5f5f5; margin: -12px -12px 10px; padding: 6px 10px; border-bottom: 1px solid #ccc; }
  .notes p { font-size: 10px; color: #555; line-height: 1.8; }
  .footer { margin-top: 30px; text-align: center; font-size: 10px; color: #999; }
  @media print {
    body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  }
</style>
</head>
<body>
<div class="title">거 래 내 역 서</div>
<table class="info-table">
  <tr>
    <td class="label">공급자</td><td>${companyName}</td>
    <td class="label-client">거래처</td><td>${clientName}</td>
  </tr>
  <tr>
    <td class="label">사업자번호</td><td>${bizNo}</td>
    <td class="label-client">사업자번호</td><td>${clientBizNo}</td>
  </tr>
  <tr>
    <td class="label">대표자</td><td>${representative}</td>
    <td class="label-client">담당자</td><td>${clientManager}</td>
  </tr>
  <tr>
    <td class="label">연락처</td><td>${companyPhone}</td>
    <td class="label-client">연락처</td><td>${clientPhone}</td>
  </tr>
  <tr>
    <td class="label">주소</td><td colspan="3">${address}</td>
  </tr>
</table>
<div style="text-align:right; margin-top:10px; margin-bottom:2px; font-size:12px; color:#888;">(*${taxType})</div>
<table class="main-table">
  <thead>
    <tr>
      <th style="width:30px">No</th>
      <th style="width:90px">날짜</th>
      <th>품목</th>
      <th style="width:50px">수량</th>
      <th style="width:80px">단가</th>
      <th style="width:90px">공급가액</th>
      <th style="width:70px">세액</th>
      <th style="width:90px">합계</th>
    </tr>
  </thead>
  <tbody>
    ${rows.map((r, i) => `<tr>
      <td class="center">${i + 1}</td>
      <td class="center">${r.date}</td>
      <td>${r.description}</td>
      <td class="right">${r.quantity.toLocaleString()}</td>
      <td class="right">${r.unitPrice.toLocaleString()}</td>
      <td class="right">${r.supplyAmount.toLocaleString()}</td>
      <td class="right">${taxType === '과세' ? r.tax.toLocaleString() : ''}</td>
      <td class="right">${r.total.toLocaleString()}</td>
    </tr>`).join('')}
    <tr class="total-row">
      <td colspan="5" style="text-align:center">합 계</td>
      <td class="right">${grandSupply.toLocaleString()}</td>
      <td class="right">${taxType === '과세' ? grandTax.toLocaleString() : ''}</td>
      <td class="right">${grandTotal.toLocaleString()}</td>
    </tr>
  </tbody>
</table>
${notes ? `<div class="notes">
  <h3>비고</h3>
  ${notes.split('\n').map(l => `<p>${l}</p>`).join('')}
</div>` : ''}
<div class="footer">${clientName || ''} 거래내역서</div>
</body>
</html>`;

    win.document.write(html);
    win.document.close();
    setTimeout(() => {
      win.print();
      win.close();
    }, 300);
  };

  const inputCls = 'px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-jeju-brown focus:border-jeju-brown';
  const labelCls = 'block text-xs text-gray-500 mb-1 font-medium';

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-light text-gray-800">거래내역서 생성</h1>
        <div className="flex gap-3">
          <button
            onClick={downloadPDF}
            className="px-6 py-2.5 bg-jeju-brown text-white rounded-lg text-sm font-medium hover:bg-jeju-brown-dark transition-colors"
          >PDF 다운로드</button>
          <button
            onClick={downloadExcel}
            className="px-6 py-2.5 border border-gray-300 text-gray-600 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors"
          >엑셀 다운로드</button>
        </div>
      </div>

      {/* 기본 정보 - 거래처 선택/관리 */}
      {clients.length > 0 && (
        <div className="flex items-center gap-3 mb-3">
          <select
            onChange={e => { const c = clients.find(c => c.id === e.target.value); if (c) selectClient(c); }}
            className={`${inputCls} text-xs`}
            defaultValue=""
          >
            <option value="" disabled>저장된 거래처 선택</option>
            {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <button onClick={saveCurrentClient} className="px-3 py-1.5 text-xs bg-jeju-brown text-white rounded-md hover:bg-jeju-brown-dark transition-colors">거래처 저장</button>
          <button onClick={() => setShowClientMgr(v => !v)} className="px-3 py-1.5 text-xs border border-gray-300 text-gray-500 rounded-md hover:bg-gray-50 transition-colors">
            {showClientMgr ? '닫기' : '거래처 관리'}
          </button>
        </div>
      )}
      {!clients.length && (
        <div className="flex items-center gap-3 mb-3">
          <button onClick={saveCurrentClient} className="px-3 py-1.5 text-xs bg-jeju-brown text-white rounded-md hover:bg-jeju-brown-dark transition-colors">거래처 저장</button>
        </div>
      )}

      {/* 거래처 관리 목록 */}
      {showClientMgr && clients.length > 0 && (
        <div className="mb-4 bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <table className="w-full text-xs">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left px-3 py-2 text-gray-500">거래처명</th>
                <th className="text-left px-3 py-2 text-gray-500">사업자번호</th>
                <th className="text-left px-3 py-2 text-gray-500">담당자</th>
                <th className="text-left px-3 py-2 text-gray-500">연락처</th>
                <th className="px-3 py-2 w-16"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {clients.map(c => (
                <tr key={c.id} className="hover:bg-gray-50">
                  <td className="px-3 py-2 cursor-pointer text-jeju-brown font-medium" onClick={() => selectClient(c)}>{c.name}</td>
                  <td className="px-3 py-2 text-gray-600">{c.biz_no || '-'}</td>
                  <td className="px-3 py-2 text-gray-600">{c.manager || '-'}</td>
                  <td className="px-3 py-2 text-gray-600">{c.phone || '-'}</td>
                  <td className="px-3 py-2 text-center">
                    <button onClick={() => deleteClient(c.id)} className="text-red-400 hover:text-red-600 text-xs">삭제</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* 공급자 + 거래처 같은 라인 */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden mb-6">
        <table className="w-full text-sm">
          <tbody>
            <tr>
              <td className="bg-gray-50 px-4 py-2.5 text-xs font-semibold text-gray-500 border border-gray-200 w-20">공급자</td>
              <td className="px-3 py-1.5 border border-gray-200">
                <input value={companyName} onChange={e => setCompanyName(e.target.value)} className={`${inputCls} w-full border-none p-0`} />
              </td>
              <td className="bg-amber-50 px-4 py-2.5 text-xs font-semibold text-gray-500 border border-gray-200 w-20">거래처</td>
              <td className="px-3 py-1.5 border border-gray-200">
                <input value={clientName} onChange={e => setClientName(e.target.value)} className={`${inputCls} w-full border-none p-0`} placeholder="거래처명" />
              </td>
            </tr>
            <tr>
              <td className="bg-gray-50 px-4 py-2.5 text-xs font-semibold text-gray-500 border border-gray-200">사업자번호</td>
              <td className="px-3 py-1.5 border border-gray-200">
                <input value={bizNo} onChange={e => setBizNo(e.target.value)} className={`${inputCls} w-full border-none p-0`} />
              </td>
              <td className="bg-amber-50 px-4 py-2.5 text-xs font-semibold text-gray-500 border border-gray-200">사업자번호</td>
              <td className="px-3 py-1.5 border border-gray-200">
                <input value={clientBizNo} onChange={e => setClientBizNo(e.target.value)} className={`${inputCls} w-full border-none p-0`} placeholder="000-00-00000" />
              </td>
            </tr>
            <tr>
              <td className="bg-gray-50 px-4 py-2.5 text-xs font-semibold text-gray-500 border border-gray-200">대표자</td>
              <td className="px-3 py-1.5 border border-gray-200">
                <input value={representative} onChange={e => setRepresentative(e.target.value)} className={`${inputCls} w-full border-none p-0`} />
              </td>
              <td className="bg-amber-50 px-4 py-2.5 text-xs font-semibold text-gray-500 border border-gray-200">담당자</td>
              <td className="px-3 py-1.5 border border-gray-200">
                <input value={clientManager} onChange={e => setClientManager(e.target.value)} className={`${inputCls} w-full border-none p-0`} placeholder="담당자명" />
              </td>
            </tr>
            <tr>
              <td className="bg-gray-50 px-4 py-2.5 text-xs font-semibold text-gray-500 border border-gray-200">연락처</td>
              <td className="px-3 py-1.5 border border-gray-200">
                <input value={companyPhone} onChange={e => setCompanyPhone(e.target.value)} className={`${inputCls} w-full border-none p-0`} />
              </td>
              <td className="bg-amber-50 px-4 py-2.5 text-xs font-semibold text-gray-500 border border-gray-200">연락처</td>
              <td className="px-3 py-1.5 border border-gray-200">
                <input value={clientPhone} onChange={e => setClientPhone(e.target.value)} className={`${inputCls} w-full border-none p-0`} placeholder="010-0000-0000" />
              </td>
            </tr>
            <tr>
              <td className="bg-gray-50 px-4 py-2.5 text-xs font-semibold text-gray-500 border border-gray-200">주소</td>
              <td className="px-3 py-1.5 border border-gray-200" colSpan={3}>
                <input value={address} onChange={e => setAddress(e.target.value)} className={`${inputCls} w-full border-none p-0`} />
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* 과세 구분 + 거래 내역 테이블 */}
      <div className="flex items-center gap-3 mb-3 mt-6">
        <label className="text-xs text-gray-500 font-medium">과세 구분</label>
        <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
          {(['과세', '면세'] as const).map(t => (
            <button
              key={t}
              onClick={() => {
                setTaxType(t);
                setRows(prev => prev.map(r => {
                  const tax = t === '과세' ? Math.round(r.supplyAmount * 0.1) : 0;
                  return { ...r, tax, total: r.supplyAmount + tax };
                }));
              }}
              className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${
                taxType === t ? 'bg-jeju-brown text-white' : 'text-gray-500'
              }`}
            >{t}</button>
          ))}
        </div>
      </div>
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left px-3 py-3 text-xs font-semibold text-gray-500 border-b border-gray-200 w-32">날짜</th>
                <th className="text-left px-3 py-3 text-xs font-semibold text-gray-500 border-b border-gray-200">품목</th>
                <th className="text-right px-3 py-3 text-xs font-semibold text-gray-500 border-b border-gray-200 w-20">수량</th>
                <th className="text-right px-3 py-3 text-xs font-semibold text-gray-500 border-b border-gray-200 w-28">단가</th>
                <th className="text-right px-3 py-3 text-xs font-semibold text-gray-500 border-b border-gray-200 w-28">공급가액</th>
                <th className="text-right px-3 py-3 text-xs font-semibold text-gray-500 border-b border-gray-200 w-24">세액</th>
                <th className="text-right px-3 py-3 text-xs font-semibold text-gray-500 border-b border-gray-200 w-28">합계</th>
                <th className="px-3 py-3 border-b border-gray-200 w-10"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.map(r => (
                <tr key={r.id} className="hover:bg-gray-50">
                  <td className="px-2 py-1.5">
                    <input type="date" value={r.date} onChange={e => updateRow(r.id, 'date', e.target.value)} className={`${inputCls} w-full`} />
                  </td>
                  <td className="px-2 py-1.5">
                    <input value={r.description} onChange={e => updateRow(r.id, 'description', e.target.value)} className={`${inputCls} w-full`} placeholder="품목명" />
                  </td>
                  <td className="px-2 py-1.5">
                    <input type="number" value={r.quantity} onChange={e => updateRow(r.id, 'quantity', Number(e.target.value))} className={`${inputCls} w-full text-right`} />
                  </td>
                  <td className="px-2 py-1.5">
                    <input type="number" value={r.unitPrice} onChange={e => updateRow(r.id, 'unitPrice', Number(e.target.value))} className={`${inputCls} w-full text-right`} />
                  </td>
                  <td className="px-3 py-2.5 text-right text-gray-800">{fmt(r.supplyAmount)}</td>
                  <td className="px-3 py-2.5 text-right text-gray-600">{taxType === '과세' ? fmt(r.tax) : '-'}</td>
                  <td className="px-3 py-2.5 text-right text-gray-800 font-medium">{fmt(r.total)}</td>
                  <td className="px-2 py-1.5 text-center">
                    <button onClick={() => removeRow(r.id)} className="text-gray-300 hover:text-red-500 transition-colors text-lg">×</button>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="bg-gray-50 font-semibold">
                <td colSpan={4} className="px-3 py-3 text-right text-gray-600 border-t border-gray-200">합계</td>
                <td className="px-3 py-3 text-right text-gray-800 border-t border-gray-200">{fmt(grandSupply)}</td>
                <td className="px-3 py-3 text-right text-gray-600 border-t border-gray-200">{taxType === '과세' ? fmt(grandTax) : '-'}</td>
                <td className="px-3 py-3 text-right text-gray-800 border-t border-gray-200">{fmt(grandTotal)}</td>
                <td className="border-t border-gray-200"></td>
              </tr>
            </tfoot>
          </table>
        </div>
        <div className="px-4 py-3 border-t border-gray-200">
          <button onClick={addRow} className="text-sm text-jeju-brown hover:underline">+ 행 추가</button>
        </div>
      </div>

      {/* 비고 */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mt-6">
        <h3 className="text-sm font-semibold text-gray-700 mb-3">비고</h3>
        <textarea
          value={notes}
          onChange={e => setNotes(e.target.value)}
          rows={9}
          className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm leading-relaxed focus:outline-none focus:ring-1 focus:ring-jeju-brown focus:border-jeju-brown"
        />
      </div>

      {toast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-jeju-brown text-white px-6 py-2.5 rounded-lg text-sm font-semibold z-50 shadow-lg">
          {toast}
        </div>
      )}
    </div>
  );
}

export default TransactionStatement;
